@import "../../global/gx-ide-mixins.scss";

:host {
  display: grid;
  block-size: 100%;
  position: relative;
}

.outer-container {
  grid-template-rows: max-content 1fr;
}
.inner-container {
  position: relative;
}

.list-box::part(item) {
  width: 100%;
  min-width: 100%;
}
.list-box::part(item__action) {
  gap: var(--spacing-gap-xs);
}
.list-box::part(item__caption) {
  font-weight: var(--font-style-semi-bold);
  color: var(--color-text-neutral-default);
  @include ellipsis();
}

// Caption row: name (flexible) + metadata pinned to the end
.list-box::part(item__inline-caption) {
  grid-template-columns: minmax(0, 1fr) max-content;
  align-items: center;
  gap: var(--spacing-gap-m);
  width: 100%;
  min-width: 100%;
}

// Group header: Mercury defaults it to body-m (14px), which reads too large
// next to the 12px item text. Keep it compact so it looks like a section label.
// The `:host` prefix raises specificity over Mercury's themed ::part rule
// (same-specificity ::part rules from the adopted theme would otherwise win).
:host .list-box::part(group__action),
:host .list-box::part(group__caption) {
  font-size: var(--font-size-body-s);
  // The expand/collapse chevron is sized by these chameleon vars (Mercury sets
  // them to --icon-s = 14px). Match the 12px header so it doesn't look oversized.
  --ch-action-list-group__expandable-button-size: var(--icon-xs);
  --ch-action-list-group__expandable-button-image-size: var(--icon-xs);
}

// Plugin logo
.list-box::part(custom-image) {
  block-size: 40px;
  inline-size: 40px;
  border-radius: var(--border-radius-m);
  object-fit: cover;
  margin-inline-end: 12px;
}

// Metadata (downloads + rating), aligned with the name
.list-box::part(custom-meta) {
  display: flex;
  align-items: center;
  gap: var(--spacing-gap-m);
}
.list-box::part(custom-meta-item) {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-gap-xs);
  font-size: var(--font-size-body-s);
  // Same emphasis as the title. Applied via `color` (not opacity) so the meta
  // icons keep their own color (e.g. the rating star).
  color: var(--color-text-neutral-default);
  white-space: nowrap;
}
.list-box::part(custom-meta-icon) {
  --ch-image-size: 14px;
}

// Content block (description + footer)
.list-box::part(custom-container) {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-gap-xs);
  width: 100%;
}
.list-box::part(item__block-end start) {
  width: 100%;
  min-width: 100%;
}
// Description — second level: lighter than the title.
.list-box::part(custom-description) {
  font-size: var(--font-size-body-s);
  margin: 0;
  color: var(--color-text-neutral-neutral);
  line-height: var(--line-height-relaxed);
  margin-inline-end: 8px;
  @include ellipsis();
}

// Footer row: publisher + action button.
// Pin the row height to the standard control height so swapping the action
// button for the install progress bar doesn't shift the layout.
.list-box::part(custom-footer) {
  display: flex;
  align-items: center;
  gap: var(--spacing-gap-m);
  width: 100%;
  margin-block-start: 2px;
  min-block-size: var(--control-block-size);
}
// Publisher — the least prominent line in the hierarchy: regular weight and
// the most muted of the three text levels (below title and description).
.list-box::part(custom-brand) {
  font-weight: var(--font-style-regular);
  font-size: var(--font-size-body-s);
  color: var(--color-text-neutral-disabled);
  @include ellipsis();
}
.list-box::part(custom-button) {
  // WA: Style the button like a Mercury primary button
  // "Slot" not working apparently.
  --button-color: var(--color-text-primary-default);
  margin-inline-start: auto;
  display: inline-grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  justify-content: center;
  padding-block: var(--control-padding-block);
  padding-inline: var(--control-padding-inline);
  box-sizing: border-box;
  color: var(--button-color);
  // Match the surrounding text size so the action doesn't look oversized.
  font-size: var(--font-size-body-s);
  font-weight: var(--font-style-semi-bold);
  text-transform: capitalize;
}
.list-box::part(custom-button):hover {
  --button-color: var(--color-text-primary-hover);
}
.list-box::part(custom-button):active {
  --button-color: var(--color-text-primary-pressed);
}

// The install progress is the shared gx-ide-progress-bar, rendered inside the
// ch-action-list shadow tree via the item's jsx callback, so it must be sized
// through ::part (like the other custom-* elements), not with plain selectors.
// A percent label sits inline with the bar so the progress reads as part of
// the row instead of a detached bar.
.list-box::part(custom-progress) {
  display: flex;
  align-items: center;
  gap: var(--spacing-gap-xs);
  margin-inline-start: auto;
  inline-size: 140px;
}
.list-box::part(custom-progress-label) {
  font-size: var(--font-size-caption-s);
  line-height: 1;
  color: var(--color-text-neutral-neutral);
  white-space: nowrap;
}
.list-box::part(custom-progress-bar) {
  flex: 1;
}
